import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { sendForgotPasswordOtp, resetPassword } from '../services/userService';
import { sendPoojariForgotPasswordOtp, resetPoojariPassword } from '../services/poojariService';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

/**
 * ============================================================
 * PASSWORD RESET - user aur poojari DONO ke liye ek hi page
 * ============================================================
 *
 * ⚠️ Do alag page kyun nahi banaye:
 *
 * Dono ka flow bilkul same hai - sirf API alag hai. Do file
 * rakhte to har bug do jagah theek karna padta, aur ek jagah
 * bhool jaana sabse aam galti hai. Role URL se aata hai:
 *
 *   /forgot-password           -> user
 *   /forgot-password/poojari   -> poojari
 *
 * ⚠️ DO STEP EK HI PAGE PE (alag route nahi):
 *
 * OTP bhejne ke baad naye page pe bhejte to email dobara type
 * karna padta. Yahan email state mein hi rehta hai.
 */
const ForgotPassword = () => {
	const navigate = useNavigate();
	const { role } = useParams();

	const isPoojari = role === 'poojari';

	// step 1 = email maango, step 2 = OTP + naya password
	const [step, setStep] = useState(1);

	const [email, setEmail] = useState('');
	const [otp, setOtp] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [loading, setLoading] = useState(false);

	const loginPath = isPoojari ? '/poojari-login' : '/login';

	// ================= STEP 1 : OTP BHEJO =================
	const handleSendOtp = async () => {
		if (!email.trim()) {
			toast.error('Please enter your email');
			return;
		}

		setLoading(true);
		try {
			if (isPoojari) {
				await sendPoojariForgotPasswordOtp(email.trim());
			} else {
				await sendForgotPasswordOtp(email.trim());
			}

			// ⚠️ Yahan "OTP sent" hi dikhana hai chahe account ho ya
			// na ho. Backend bhi dono case mein safal jawab deta hai.
			// "No account found" dikhate to koi bhi email daal ke
			// pata laga leta ki us email ka account hai ya nahi.
			toast.success('If this email is registered, an OTP has been sent.');
			setStep(2);
		} catch (err) {
			toast.error(err.response?.data?.message || 'Could not send OTP. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	// ============ STEP 2 : PASSWORD BADLO ============
	const handleResetPassword = async () => {
		if (otp.trim().length !== 6) {
			toast.error('Please enter the 6-digit OTP');
			return;
		}

		// ⚠️ Minimum 8 - backend ke @Size(min = 8) se match karta hai
		if (newPassword.length < 8) {
			toast.error('Password must be at least 8 characters');
			return;
		}

		if (newPassword !== confirmPassword) {
			toast.error('Passwords do not match');
			return;
		}

		setLoading(true);
		try {
			if (isPoojari) {
				await resetPoojariPassword(email.trim(), otp.trim(), newPassword);
			} else {
				await resetPassword(email.trim(), otp.trim(), newPassword);
			}

			toast.success('Password changed. Please login with your new password.');

			// ⚠️ Login page pe bhejna zaroori - backend ne saare
			// purane session revoke kar diye hain
			navigate(loginPath, { replace: true });
		} catch (err) {
			toast.error(err.response?.data?.message || 'Could not reset password. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const labelStyle = {
		color: DARK_RED,
		fontSize: '13px',
		fontWeight: 600
	};

	const inputStyle = { borderColor: GOLD };

	const primaryButtonStyle = {
		backgroundColor: RED,
		color: GOLD,
		border: 'none',
		borderRadius: '10px'
	};

	return (
		<div style={{ backgroundColor: '#FFF5F5' }} className="pb-screen">

			{/* ===== HEADER ===== */}
			<div style={{
				backgroundColor: DARK_RED,
				padding: '14px 16px'
			}}>
				<div className="d-flex align-items-center gap-2">
					<button
						onClick={() => navigate(loginPath)}
						style={{
							background: 'none',
							border: 'none',
							color: GOLD,
							fontSize: '22px',
							lineHeight: 1,
							padding: 0
						}}>
						←
					</button>
					<div>
						<p className="mb-0 fw-bold"
							style={{
								color: GOLD,
								fontSize: '15px'
							}}>
							🔑 Reset password
						</p>
						<p className="mb-0"
							style={{
								color: '#FFE4B5',
								fontSize: '12px'
							}}>
							{isPoojari ? 'Poojari account' : 'User account'}
						</p>
					</div>
				</div>
			</div>

			<div className="container" style={{ maxWidth: '480px' }}>
				<div className="py-4">

					<div className="p-3 mb-4"
						style={{
							backgroundColor: 'white',
							border: `1px solid ${GOLD}`,
							borderRadius: '12px'
						}}>

						{step === 1 && (
							<>
								<p className="mb-3"
									style={{
										color: '#666',
										fontSize: '13px'
									}}>
									Enter your registered email. We'll send you a 6-digit OTP.
								</p>

								<div className="mb-3">
									<label className="form-label" style={labelStyle}>
										Registered email
									</label>
									<input
										type="email"
										className="form-control"
										style={inputStyle}
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										placeholder="you@example.com"
										disabled={loading} />
								</div>

								<button
									type="button"
									className="btn w-100 fw-bold py-2"
									style={primaryButtonStyle}
									onClick={handleSendOtp}
									disabled={loading}>
									{loading ? 'Sending...' : 'Send OTP'}
								</button>
							</>
						)}

						{step === 2 && (
							<>
								<p className="mb-3"
									style={{
										color: '#666',
										fontSize: '13px'
									}}>
									OTP sent to <strong style={{ color: DARK_RED }}>{email}</strong>.
									It is valid for 10 minutes.
								</p>

								<div className="mb-3">
									<label className="form-label" style={labelStyle}>
										6-digit OTP
									</label>
									<input
										type="text"
										inputMode="numeric"
										maxLength={6}
										className="form-control text-center fw-bold"
										style={{
											...inputStyle,
											letterSpacing: '8px',
											fontSize: '18px'
										}}
										value={otp}
										onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
										placeholder="000000"
										disabled={loading} />
								</div>

								<div className="mb-3">
									<label className="form-label" style={labelStyle}>
										New password
									</label>
									<input
										type="password"
										className="form-control"
										style={inputStyle}
										value={newPassword}
										onChange={(e) => setNewPassword(e.target.value)}
										placeholder="At least 8 characters"
										disabled={loading} />
								</div>

								<div className="mb-3">
									<label className="form-label" style={labelStyle}>
										Confirm new password
									</label>
									<input
										type="password"
										className="form-control"
										style={inputStyle}
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										placeholder="Re-enter new password"
										disabled={loading} />
								</div>

								<button
									type="button"
									className="btn w-100 fw-bold py-2 mb-2"
									style={primaryButtonStyle}
									onClick={handleResetPassword}
									disabled={loading}>
									{loading ? 'Changing...' : 'Change Password'}
								</button>

								{/* OTP na aaye to wapas jaake dobara bhej sakta hai */}
								<button
									type="button"
									className="btn btn-link w-100 p-0"
									style={{
										color: RED,
										fontWeight: 600,
										fontSize: '13px',
										textDecoration: 'none'
									}}
									onClick={() => setStep(1)}
									disabled={loading}>
									Didn't get the OTP? Try again
								</button>
							</>
						)}
					</div>

					<div className="text-center">
						<small className="text-muted">
							Remembered your password?{' '}
							<button
								type="button"
								className="btn btn-link p-0 align-baseline"
								style={{
									color: RED,
									fontWeight: 600,
									textDecoration: 'none'
								}}
								onClick={() => navigate(loginPath)}>
								Back to login
							</button>
						</small>
					</div>

				</div>
			</div>
		</div>
	);
};

export default ForgotPassword;